/**
 * Image and video models people can pick. The provider (ByteDance, Higgsfield,
 * OpenRouter) stays on the row. Callers route from `provider`, not from a
 * separate provider picker.
 */
import { arkEnabled } from "./mediaProvider";
import { isSelfValidatedVideoModelId } from "./upscaleIds";
import { OPENROUTER_IMAGE_MODEL_IDS } from "./openRouterImages";
import {
  VIDEO_MODELS,
  validateVideoModelCapabilities,
  type VideoCapabilityRequest,
  type VideoModelDef,
  type VideoReferenceKind,
} from "./videoModels";

export type MediaProvider = "ark" | "higgsfield" | "openrouter";
export type MediaModality = "image" | "video";
export type MediaPayWith = "studio" | "higgsfield";
export type MediaLogo = "bytedance" | "higgsfield" | "openai";

export type MediaModelDef = {
  id: string;
  label: string;
  vendor: string;
  logo: MediaLogo;
  blurb: string;
  goodFor: string;
  willNot: string;
  modality: MediaModality;
  provider: MediaProvider;
  /** Higgsfield path without a leading slash. */
  endpointPath?: string;
  /** Floor quote in Studio credits when Higgsfield estimate is not available yet. */
  quoteCredits: number;
  allowsPersonalKey: boolean;
  supportsPrompt: boolean;
  supportsStartFrame: boolean;
  supportsEndFrame: boolean;
  supportsDuration: boolean;
  supportsAspect: boolean;
  supportsResolution: boolean;
  supportsQuality: boolean;
  supportsAudio: boolean;
  requiresVideoReference: boolean;
  requiresImageReference: boolean;
  /** Studio-credit jobs may retry this model if Higgsfield never accepted the request. */
  fallbackModelId?: string;
  isDefault?: boolean;
  maxDurationSeconds?: number;
  durationPresets?: number[];
};

const SEEDANCE_25 = VIDEO_MODELS.find((model) => model.slug === "seedance-2.5")!;
const SEEDANCE_20 = VIDEO_MODELS.find((model) => model.slug === "seedance-2.0")!;
const SEEDANCE_20_FAST = VIDEO_MODELS.find((model) => model.slug === "seedance-2.0-fast")!;
const SEEDANCE_20_MINI = VIDEO_MODELS.find((model) => model.slug === "seedance-2.0-mini")!;

function fromSeedance(model: VideoModelDef, isDefault: boolean, blurb?: string): MediaModelDef {
  return {
    id: model.slug,
    label: model.label,
    vendor: "ByteDance",
    logo: "bytedance",
    endpointPath: model.gatewayModelId,
    blurb:
      blurb ??
      (isDefault
        ? "Default. Motion and camera, up to 30s."
        : "Sharper detail through 4K, up to 15s."),
    goodFor: blurb
      ? blurb
      : isDefault
        ? "Motion, light, and camera up to 30s."
        : "Sharper detail through 4K. Clips up to 15s.",
    willNot: "No motion transfer from a reference video.",
    modality: "video",
    provider: "ark",
    quoteCredits: 0,
    allowsPersonalKey: false,
    supportsPrompt: true,
    supportsStartFrame: true,
    supportsEndFrame: model.supportsLastFrame,
    supportsDuration: true,
    supportsAspect: true,
    supportsResolution: true,
    supportsQuality: false,
    supportsAudio: true,
    requiresVideoReference: false,
    requiresImageReference: false,
    isDefault,
    maxDurationSeconds: model.maxDurationSeconds,
    durationPresets: model.durationPresets,
  };
}

type MediaSeed = Omit<
  MediaModelDef,
  | "supportsPrompt"
  | "supportsStartFrame"
  | "supportsEndFrame"
  | "supportsDuration"
  | "supportsAspect"
  | "supportsResolution"
  | "supportsQuality"
  | "supportsAudio"
  | "requiresVideoReference"
  | "requiresImageReference"
  | "quoteCredits"
  | "allowsPersonalKey"
> &
  Partial<
    Pick<
      MediaModelDef,
      | "supportsPrompt"
      | "supportsStartFrame"
      | "supportsDuration"
      | "supportsAspect"
      | "supportsResolution"
      | "supportsQuality"
      | "supportsAudio"
      | "requiresVideoReference"
      | "requiresImageReference"
      | "quoteCredits"
      | "allowsPersonalKey"
      | "supportsEndFrame"
    >
  >;

function mediaRow(seed: MediaSeed): MediaModelDef {
  const image = seed.modality === "image";
  return {
    supportsPrompt: true,
    supportsStartFrame: false,
    supportsEndFrame: false,
    supportsDuration: false,
    supportsAspect: image && seed.provider === "ark",
    supportsResolution: image && seed.provider === "ark",
    supportsQuality: false,
    supportsAudio: false,
    requiresVideoReference: false,
    requiresImageReference: false,
    quoteCredits: seed.provider === "higgsfield" ? (image ? 4 : 16) : 0,
    allowsPersonalKey: seed.provider === "higgsfield",
    ...seed,
  };
}

export const MEDIA_MODELS: readonly MediaModelDef[] = [
  fromSeedance(SEEDANCE_25, true),
  fromSeedance(SEEDANCE_20, false),
  fromSeedance(SEEDANCE_20_FAST, false, "Faster Seedance 2.0. Up to 15s at 720p."),
  fromSeedance(SEEDANCE_20_MINI, false, "Smaller Seedance 2.0. Up to 15s at 720p."),
  mediaRow({
    id: "genjutsu-motion",
    label: "Genjutsu",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Motion transfer onto a still.",
    goodFor: "Moves a still the way the source video moves.",
    willNot: "Needs a video and one image.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "higgsfield/genjutsu/motion-transfer/v1.0",
    supportsPrompt: false,
    requiresVideoReference: true,
    requiresImageReference: true,
  }),
  mediaRow({
    id: "genjutsu-swap",
    label: "Genjutsu Swap",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Swap an object in a video.",
    goodFor: "Replaces an object using a reference image.",
    willNot: "Needs a video and one image.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "higgsfield/genjutsu/object-swap/v1.0",
    supportsPrompt: false,
    requiresVideoReference: true,
    requiresImageReference: true,
  }),
  mediaRow({
    id: "genjutsu-restyle",
    label: "Genjutsu Restyle",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Restyle a source video.",
    goodFor: "Keeps the motion and changes the look.",
    willNot: "Needs a video and one image.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "higgsfield/genjutsu/restyle/v1.0",
    supportsPrompt: false,
    requiresVideoReference: true,
    requiresImageReference: true,
  }),
  mediaRow({
    id: "cinema-studio-4",
    label: "Cinema Studio 4",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Styled shots with camera control.",
    goodFor: "Camera and style on a written prompt.",
    willNot: "Not a motion-transfer model.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "higgsfield/cinema-studio/4.0",
  }),
  mediaRow({
    id: "kling-3",
    label: "Kling 3.0",
    vendor: "Kling",
    logo: "higgsfield",
    blurb: "Text to video. About 5 seconds.",
    goodFor: "Prompted clips through Higgsfield.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "kling-video/v3.0/std/text-to-video",
  }),
  mediaRow({
    id: "kling-2.6",
    label: "Kling 2.6",
    vendor: "Kling",
    logo: "higgsfield",
    blurb: "Text to video, with sound.",
    goodFor: "Prompted clips through Higgsfield.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "kling-video/v2.6/pro/text-to-video",
  }),
  mediaRow({
    id: "kling-2.5-turbo",
    label: "Kling 2.5 Turbo",
    vendor: "Kling",
    logo: "higgsfield",
    blurb: "Faster Kling text to video.",
    goodFor: "Prompted clips through Higgsfield.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "kling-video/v2.5-turbo/pro/text-to-video",
  }),
  mediaRow({
    id: "wan-3",
    label: "Wan 3.0",
    vendor: "Alibaba",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "alibaba/wan-3.0/text-to-video",
  }),
  mediaRow({
    id: "wan-2.6",
    label: "Wan 2.6",
    vendor: "Alibaba",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "wan/v2.6/text-to-video",
  }),
  mediaRow({
    id: "happy-horse",
    label: "Happy Horse",
    vendor: "Alibaba",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "alibaba/happy-horse/text-to-video",
  }),
  mediaRow({
    id: "ltx-2.5",
    label: "LTX 2.5",
    vendor: "Lightricks",
    logo: "higgsfield",
    blurb: "Fast text to video.",
    goodFor: "Prompted clips through Higgsfield.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "lightricks/ltx-2.5/text-to-video/fast",
  }),
  mediaRow({
    id: "minimax-h3",
    label: "MiniMax H3",
    vendor: "MiniMax",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "minimax/h3/text-to-video",
  }),
  mediaRow({
    id: "hailuo-2.3",
    label: "Hailuo 2.3",
    vendor: "MiniMax",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "minimax/hailuo-2.3/standard/text-to-video",
  }),
  mediaRow({
    id: "pixverse-v6",
    label: "PixVerse V6",
    vendor: "PixVerse",
    logo: "higgsfield",
    blurb: "Text to video through Higgsfield.",
    goodFor: "Prompted clips.",
    willNot: "Duration stays on the model default.",
    modality: "video",
    provider: "higgsfield",
    endpointPath: "pixverse/v6/text-to-video",
  }),
  mediaRow({
    id: "gpt-image-2.5-sunburst",
    label: "GPT Image 2.5",
    vendor: "OpenAI",
    logo: "openai",
    blurb: "Default. Everyday stills and edits.",
    goodFor: "Portraits, products, and edits from stills.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "openrouter",
    endpointPath: OPENROUTER_IMAGE_MODEL_IDS.image,
    supportsAspect: true,
    supportsResolution: true,
    supportsQuality: true,
    isDefault: true,
  }),
  mediaRow({
    id: "seedream-5.0-pro",
    label: "Seedream 5.0 Pro",
    vendor: "ByteDance",
    logo: "bytedance",
    blurb: "ByteDance stills. References and text.",
    goodFor: "Precise stills from a prompt or references.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "ark",
    endpointPath: "dola-seedream-5-0-pro-260628",
  }),
  mediaRow({
    id: "seedream-5.0-flash",
    label: "Seedream 5.0 Flash",
    vendor: "ByteDance",
    logo: "bytedance",
    blurb: "Faster Seedream stills.",
    goodFor: "Quicker stills from a prompt or references.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "ark",
    endpointPath: "dola-seedream-5-0-flash-260915",
  }),
  mediaRow({
    id: "seedream-5.0-lite",
    label: "Seedream 5.0 Lite",
    vendor: "ByteDance",
    logo: "bytedance",
    blurb: "Lighter Seedream stills.",
    goodFor: "Stills from a prompt or references.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "ark",
    endpointPath: "seedream-5-0-lite-260128",
  }),
  mediaRow({
    id: "seedream-4.5",
    label: "Seedream 4.5",
    vendor: "ByteDance",
    logo: "bytedance",
    blurb: "Seedream 4.5 stills.",
    goodFor: "Stills from a prompt or references.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "ark",
    endpointPath: "seedream-4-5-251128",
  }),
  mediaRow({
    id: "seedream-4.0",
    label: "Seedream 4.0",
    vendor: "ByteDance",
    logo: "bytedance",
    blurb: "Seedream 4.0 stills.",
    goodFor: "Stills from a prompt or references.",
    willNot: "Does not make video.",
    modality: "image",
    provider: "ark",
    endpointPath: "seedream-4-0-250828",
  }),
  mediaRow({
    id: "soul-v2",
    label: "Soul 2",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Editorial stills from a prompt.",
    goodFor: "Portraits and editorial photos from a prompt.",
    willNot: "Falls back to GPT Image if it cannot start.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "higgsfield-ai/soul/v2/standard",
    fallbackModelId: "gpt-image-2.5-sunburst",
  }),
  mediaRow({
    id: "soul",
    label: "Soul",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Styled stills from a prompt.",
    goodFor: "Images with a Soul style.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "higgsfield-ai/soul/standard",
  }),
  mediaRow({
    id: "soul-cinema",
    label: "Soul Cinema",
    vendor: "Higgsfield",
    logo: "higgsfield",
    blurb: "Cinema stills from a prompt.",
    goodFor: "Film-like photographs.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "higgsfield-ai/soul/cinema",
  }),
  mediaRow({
    id: "grok-image-2",
    label: "Grok Image 2",
    vendor: "xAI",
    logo: "higgsfield",
    blurb: "Stills, with optional references.",
    goodFor: "Generate or edit from a prompt.",
    willNot: "Runs through Higgsfield.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "xai/grok-imagine-image-2.0",
  }),
  mediaRow({
    id: "z-image-turbo",
    label: "Z-Image Turbo",
    vendor: "Alibaba",
    logo: "higgsfield",
    blurb: "Fast stills from a prompt.",
    goodFor: "Quick images through Higgsfield.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "z-image/turbo",
  }),
  mediaRow({
    id: "ideogram-4",
    label: "Ideogram 4",
    vendor: "Ideogram",
    logo: "higgsfield",
    blurb: "Stills from a prompt.",
    goodFor: "Images through Higgsfield.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "ideogram/v4.0",
  }),
  mediaRow({
    id: "recraft-4.1",
    label: "Recraft 4.1",
    vendor: "Recraft",
    logo: "higgsfield",
    blurb: "1K stills from a prompt.",
    goodFor: "Images through Higgsfield.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "recraft/v4.1/text-to-image",
  }),
  mediaRow({
    id: "qwen-image-3",
    label: "Qwen Image 3",
    vendor: "Alibaba",
    logo: "higgsfield",
    blurb: "Stills from a prompt.",
    goodFor: "Images through Higgsfield.",
    willNot: "Prompt only.",
    modality: "image",
    provider: "higgsfield",
    endpointPath: "alibaba/qwen-image-3/text-to-image",
  }),
];

export function mediaModelById(id: string | null | undefined): MediaModelDef | null {
  const key = id?.trim();
  if (!key) return null;
  return (
    MEDIA_MODELS.find(
      (model) => model.id === key || model.endpointPath === key,
    ) ?? null
  );
}

/**
 * Id to store on the job for a picker row.
 * Higgsfield and Seedream must stay their catalog ids — the gateway looks the row up again.
 * Returns null for Seedance video and GPT Image so the caller keeps its existing gateway id.
 */
export function jobModelIdForPick(
  mode: "image" | "video",
  videoModel?: string | null,
  imageModel?: string | null,
): string | null {
  const picked = mediaModelById(mode === "video" ? videoModel : imageModel);
  if (!picked || picked.modality !== mode) return null;
  if (picked.provider === "higgsfield") return picked.id;
  if (picked.provider === "ark" && picked.modality === "image") return picked.id;
  return null;
}

export function defaultMediaModel(modality: MediaModality): MediaModelDef {
  return (
    MEDIA_MODELS.find((model) => model.modality === modality && model.isDefault) ??
    MEDIA_MODELS.find((model) => model.modality === modality)!
  );
}

/** ARK-only rows (Seedream images, Seedance 2.0 Fast/Mini): no Higgsfield endpoint. */
export function isArkOnlyMediaModel(model: MediaModelDef): boolean {
  return model.provider === "ark" && model.id !== "seedance-2.5" && model.id !== "seedance-2.0";
}

export function listMediaModels(modality: MediaModality): MediaModelDef[] {
  return MEDIA_MODELS.filter(
    (model) => model.modality === modality && (arkEnabled() || !isArkOnlyMediaModel(model)),
  );
}

export function isHiggsfieldMediaId(id: string | null | undefined): boolean {
  return mediaModelById(id)?.provider === "higgsfield";
}

export function assertHiggsfieldVideoReady(
  model: MediaModelDef,
  request: {
    referenceKinds?: readonly VideoReferenceKind[];
    hasMultimodalReferences?: boolean;
  },
): void {
  if (model.modality !== "video" || model.provider !== "higgsfield") return;
  const kinds = request.referenceKinds ?? [];
  if (model.requiresVideoReference && !kinds.includes("video")) {
    throw new Error(`${model.label} needs a video and a reference image.`);
  }
  if (
    model.requiresImageReference &&
    !kinds.includes("image") &&
    !request.hasMultimodalReferences
  ) {
    throw new Error(`${model.label} needs a video and a reference image.`);
  }
}

/** Seedance goes through the video catalog. Higgsfield rows must not hit that check — it still rejects anything with "kling" in the id. */
export function assertVideoJobModel(
  id: string | null | undefined,
  request: VideoCapabilityRequest,
): void {
  // Upscale and Aleph jobs validate their own limits (lib/upscale.ts, lib/videoWiden.ts).
  if (isSelfValidatedVideoModelId(id)) return;
  const picked = mediaModelById(id);
  if (picked?.provider === "higgsfield" && picked.modality === "video") {
    if (request.referenceKinds) assertHiggsfieldVideoReady(picked, request);
    return;
  }
  validateVideoModelCapabilities(id, request);
}

export type MediaModelPublic = {
  slug: string;
  label: string;
  vendor: string;
  blurb: string;
  goodFor: string;
  willNot: string;
  provider: MediaProvider;
  modality: MediaModality;
  allowsPersonalKey: boolean;
  isDefault: boolean;
  requiresVideoReference: boolean;
  requiresImageReference: boolean;
  supportsDuration: boolean;
  supportsStartFrame: boolean;
  maxDurationSeconds?: number;
  description: string;
  requiresStartFrame: boolean;
  supportsMultimodalRefs: boolean;
  supportsLastFrame: boolean;
  resolutions: string[];
  mcpOnly: boolean;
};

export function toPublicMediaModel(model: MediaModelDef): MediaModelPublic {
  const seedance = VIDEO_MODELS.find((row) => row.slug === model.id);
  return {
    slug: model.id,
    label: model.label,
    vendor: model.vendor,
    blurb: model.blurb,
    goodFor: model.goodFor,
    willNot: model.willNot,
    provider: model.provider,
    modality: model.modality,
    allowsPersonalKey: model.allowsPersonalKey,
    isDefault: Boolean(model.isDefault),
    requiresVideoReference: model.requiresVideoReference,
    requiresImageReference: model.requiresImageReference,
    supportsDuration: model.supportsDuration,
    supportsStartFrame: model.supportsStartFrame,
    ...(model.maxDurationSeconds ? { maxDurationSeconds: model.maxDurationSeconds } : {}),
    description: model.blurb,
    requiresStartFrame: false,
    supportsMultimodalRefs: seedance?.supportsMultimodalRefs ?? model.requiresVideoReference,
    supportsLastFrame: model.supportsEndFrame,
    resolutions: seedance?.resolutionOptions.map((option) => option.gatewayLabel) ?? [],
    mcpOnly: false,
  };
}

export function listMediaModelsPublic(modality: MediaModality): MediaModelPublic[] {
  return listMediaModels(modality).map(toPublicMediaModel);
}
